import { useState, useEffect } from 'react';

// Interfaces para los datos de los combos
interface Ingredient {
  id: string;
  name: string;
  unit: string;
  category: string;
}

interface Location {
  id: string;
  name: string;
  address: string;
}

interface MovementType {
  id: string;
  name: string;
  direction: 'entry' | 'exit';
  requiresApproval: boolean;
}

interface ComboData {
  ingredients: Ingredient[];
  locations: Location[];
  entryTypes: MovementType[];
  exitTypes: MovementType[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Función para obtener el token de autenticación
const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

// Función para hacer peticiones autenticadas
const apiRequest = async (endpoint: string): Promise<any> => {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
    throw new Error(errorData.error || `HTTP Error: ${response.status}`);
  }

  return response.json();
};

export const useComboData = () => {
  const [comboData, setComboData] = useState<ComboData>({
    ingredients: [],
    locations: [],
    entryTypes: [],
    exitTypes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadComboData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cargar datos en paralelo
        const [ingredientsResponse, locationsResponse, movementTypesResponse] = await Promise.all([
          apiRequest('/ingredients?limit=500'),
          apiRequest('/locations'),
          apiRequest('/movement-types')
        ]);

        // Mapear ingredientes
        const ingredients: Ingredient[] = ingredientsResponse.data.map((ingredient: any) => ({
          id: ingredient.id.toString(),
          name: ingredient.name,
          unit: ingredient.unitDisplayName || ingredient.unitSymbol || ingredient.unit || 'kg',
          category: ingredient.categoryName || 'General'
        }));

        // Mapear locaciones
        const locations: Location[] = locationsResponse.data.map((location: any) => ({
          id: location.id.toString(),
          name: location.nombre,
          address: location.direccion || ''
        }));

        // Mapear tipos de movimiento desde el API
        const allMovementTypes: MovementType[] = movementTypesResponse.data.map((type: any) => ({
          id: type.code,
          name: type.name,
          direction: type.direction,
          requiresApproval: type.requiresApproval
        }));

        // Separar por dirección
        const entryTypes = allMovementTypes.filter(type => type.direction === 'entry');
        const exitTypes = allMovementTypes.filter(type => type.direction === 'exit');

        setComboData({
          ingredients,
          locations,
          entryTypes,
          exitTypes
        });

      } catch (err) {
        console.error('Error loading combo data:', err);
        setError('Error al cargar datos de combos');
      } finally {
        setLoading(false);
      }
    };

    loadComboData();
  }, []);

  return {
    comboData,
    loading,
    error,
    // Helper functions para acceso directo
    getIngredientById: (id: string) => comboData.ingredients.find(ing => ing.id === id),
    getLocationById: (id: string) => comboData.locations.find(loc => loc.id === id),
    getMovementTypeById: (id: string) => [...comboData.entryTypes, ...comboData.exitTypes].find(type => type.id === id)
  };
};